<template>
    <!-- fullscreen: a Vuetify dialog (it takes care of layering and styling); else over the positioned parent -->
    <component :is="fullscreen ? 'v-dialog' : 'div'" v-bind="wrapper" v-on="wrapperEvents">
        <div class="lightbox" :class="{ 'lightbox-full': fullscreen }">
            <div v-if="!fullscreen" class="image-overlay" :style="box"></div>

            <div class="image-zoom" :style="box">
                <div class="lightbox-buttons">
                    <v-btn fab color="primary" small @click="$emit('close')">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>

                    <v-btn fab color="primary" small class="mt-3" @click="onMax">
                        <v-icon>mdi-arrow-expand-all</v-icon>
                    </v-btn>

                    <v-btn fab color="primary" small class="mt-3" @click="onDownload">
                        <v-icon>mdi-download</v-icon>
                    </v-btn>
                </div>
                <v-carousel
                    hide-delimiters
                    show-arrows-on-hover
                    :height="fullscreen ? '100vh' : height + 10"
                    :class="{ 'mt-5': !fullscreen }"
                    :value="position"
                    @change="$emit('update:position', $event)"
                >
                    <v-carousel-item v-for="image in images" :key="image.id">
                        <img-image :id="image.id" :item="image" :image="true" :fill="fullscreen" />
                    </v-carousel-item>
                </v-carousel>
            </div>
        </div>
    </component>
</template>

<script>
import Core from "@/modules/core/service/Core";
import Img from "@/modules/img/components/service/Img";
import ImgImage from "@/modules/img/components/main/ImgImage";
import FolderDownload from "@/modules/img/components/service/FolderDownload";

/**
 * The lightbox over a gallery: a carousel of [images] (entries with renditions) at [position] (sync),
 * with close, full screen and download. Left/right arrow keys browse, ESC closes. It sits where it is
 * in the flow, over the positioned parent.
 */
export default {
    name: "ImgLightbox",

    components: { ImgImage },

    props: {
        images  : { type: Array, required: true },
        position: { type: Number, default: 0 },
        height  : { type: Number, default: 740 },
        /** Covers the whole window instead of the positioned parent. */
        fullscreen: { type: Boolean, default: false },
    },

    computed: {
        box () {
            return this.fullscreen ? {} : { height: this.height + "px" };
        },

        wrapper () {
            return this.fullscreen
                ? { value: true, fullscreen: true, hideOverlay: true, contentClass: "lightbox-dialog" }
                : {};
        },

        wrapperEvents () {
            return this.fullscreen ? { input: () => this.$emit("close") } : {};
        }
    },

    mounted () {
        window.addEventListener("keydown", this.onKey);
    },

    beforeDestroy () {
        window.removeEventListener("keydown", this.onKey);
    },

    methods: {
        onKey (event) {
            if (event.target && ["INPUT", "TEXTAREA", "SELECT"].includes(event.target.tagName)) {
                return;
            }
            const count = this.images.length;
            if (event.key === "Escape") {
                this.$emit("close");
            } else if (event.key === "ArrowLeft" && count > 0) {
                this.$emit("update:position", (this.position - 1 + count) % count);
            } else if (event.key === "ArrowRight" && count > 0) {
                this.$emit("update:position", (this.position + 1) % count);
            } else {
                return;
            }
            event.preventDefault();
        },

        onMax () {
            Img.fullscreen(this, this.images[this.position]);
        },

        onDownload () {
            FolderDownload.image(this.images[this.position]).catch(() => {
                Core.message.hint(this, this.$t("img.download.failed"));
            });
        },
    }
}
</script>

<style scoped>
.lightbox-buttons {
    position: absolute;
    right: 25px;
    top: 22px;
    z-index: 999;
    width: 25px;
}

.image-zoom, .image-overlay {
    position: absolute;
    width: calc(100% - 70px);
    z-index: 10;
}

.image-zoom {
    max-width: 62%;
    margin-left: 8%;
}

.image-overlay {
    z-index: 2;
    opacity: 0.75;
    background-color: white;
}

.lightbox-full {
    position: relative;
    width: 100%;
    height: 100%;
}

.lightbox-full .image-zoom {
    position: relative;
    width: 100%;
    max-width: none;
    margin-left: 0;
}
</style>

<style>
.lightbox-dialog {
    background-color: black;
}
</style>
